/**
 * src/services/orders.js
 * Order creation via Supabase RPC (create_order).
 * Falls back to pure WhatsApp flow if Supabase is not configured.
 */
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';

/**
 * Create an order via the create_order RPC.
 *
 * Payload shape:
 * {
 *   customer_name: string,
 *   phone: string,
 *   address?: string,
 *   city?: string,
 *   notes?: string,
 *   language?: 'en' | 'ar',
 *   shipping_fee?: number,
 *   discount?: number,
 *   items: Array<{
 *     product_id: string,   // UUID from Supabase OR legacy slug
 *     size_id?: string,     // e.g. '50ml'
 *     quantity: number,
 *   }>
 * }
 *
 * Returns: { order_id, order_number, total }
 * Throws on validation errors.
 */
export async function createOrder(payload) {
  if (!isSupabaseConfigured) {
    // Static fallback: return a fake order number for WhatsApp-only flow
    const fakeCode = `LTH-${Date.now().toString().slice(-6)}`;
    return {
      order_id: null,
      order_number: fakeCode,
      order_code: fakeCode,
      total: payload.items?.reduce((s, i) => s + (i.unit_price ?? 0) * i.quantity, 0) ?? 0,
    };
  }

  const { data, error } = await supabase.rpc('create_order', { payload });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Fetch all orders for admin (staff only via RLS).
 */
export async function fetchOrders({ page = 1, perPage = 20, status = null, search = '' } = {}) {
  if (!isSupabaseConfigured) return { data: [], count: 0 };

  let query = supabase
    .from('orders')
    .select('*, order_items(*)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * perPage, page * perPage - 1);

  if (status) {
    if (status === 'in_progress') {
      query = query.in('status', ['preparing', 'confirmed', 'shipped']);
    } else {
      query = query.eq('status', status);
    }
  }
  if (search) {
    query = query.or(
      `customer_name.ilike.%${search}%,phone.ilike.%${search}%,order_number::text.ilike.%${search}%`
    );
  }

  const { data, error, count } = await query;
  if (error) throw error;
  return { data: data ?? [], count: count ?? 0 };
}

/**
 * Fetch a single order with full details.
 */
export async function fetchOrder(orderId) {
  if (!isSupabaseConfigured) return null;

  const { data, error } = await supabase
    .from('orders')
    .select('*, order_items(*), order_status_history(*)')
    .eq('id', orderId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update order status (calls update_order_status RPC).
 */
export async function updateOrderStatus(orderId, status, note = null) {
  if (!isSupabaseConfigured || !supabase) return;

  try {
    const { error } = await supabase.rpc('update_order_status', {
      p_order_id: orderId,
      p_status: status,
      p_note: note,
    });
    if (!error) return;
  } catch (rpcErr) {
    console.warn('update_order_status RPC notice:', rpcErr);
  }

  // Fallback to direct table update with RLS
  const { error: updateErr } = await supabase
    .from('orders')
    .update({ status })
    .eq('id', orderId);

  if (updateErr) throw updateErr;
}

/**
 * Subscribe to realtime new orders (admin only).
 * Returns an unsubscribe function.
 */
export function subscribeToOrders(callback) {
  if (!isSupabaseConfigured || !supabase) return () => {};

  const channel = supabase
    .channel('orders-realtime')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'orders' }, callback)
    .subscribe();

  return () => supabase.removeChannel(channel);
}


/**
 * Fetch last order customer details by phone number (for auto-filling checkout).
 */
export async function fetchLastOrderByPhone(phone) {
  if (!isSupabaseConfigured || !phone) return null;
  const cleanPhone = phone.trim();
  if (!cleanPhone) return null;
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('customer_name, phone, address, city')
      .eq('phone', cleanPhone)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;
    return {
      name: data.customer_name || '',
      phone: data.phone || cleanPhone,
      address: data.address || '',
      city: data.city || '',
    };
  } catch {
    return null;
  }
}

/**
 * Fetch current authenticated Supabase user profile for auto-filling checkout.
 */
export async function getCurrentCustomerData() {
  if (!isSupabaseConfigured || !supabase) return null;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const meta = user.user_metadata || {};
    return {
      name: meta.full_name || meta.name || '',
      phone: meta.phone || user.phone || '',
      address: meta.address || '',
      city: meta.city || '',
    };
  } catch {
    return null;
  }
}
